import React, { useState, useMemo } from 'react';
import styles from './Catalogo.module.css';

const ChevronIcon = ({ open }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const FilterIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

const FilterSection = ({ title, items, activeItems, onToggle }) => {
  const [isOpen, setIsOpen] = useState(true);

  if (!items || items.length === 0) return null;

  return (
    <div className={styles.filterGroup}>
      <button 
        className={styles.filterGroupHeader} 
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span className={styles.filterGroupTitle}>{title}</span>
        <ChevronIcon open={isOpen} />
      </button>
      
      {isOpen && (
        <div className={styles.filterGroupContent}>
          {items.map(item => {
            const isActive = activeItems.includes(item.id);
            return (
              <label
                key={item.id}
                className={`${styles.filterCheckboxItem} ${isActive ? styles.filterCheckboxItemActive : ''}`}
              >
                <input
                  type="checkbox"
                  className={styles.filterCheckboxInput}
                  checked={isActive}
                  onChange={() => {
                    if (isActive) {
                      onToggle(activeItems.filter(id => id !== item.id));
                    } else {
                      onToggle([...activeItems, item.id]);
                    }
                  }}
                />
                <span className={styles.filterItemName}>{item.name}</span>
                {item.count > 0 && (
                  <span className={styles.filterItemCount}>{item.count}</span>
                )}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const PanelFiltros = ({ products, categories, activeFilters, onFilterChange, onClearFilters }) => {
  const [mobileOpen, setMobileOpen] = useState(true);

  const filterOptions = useMemo(() => {
    // Helper to get products matching all groups EXCEPT the specified groupToIgnore
    const getProductsExcludingGroup = (groupToIgnore) => {
      return products.filter(p => {
        const matchCat = groupToIgnore === 'categorias' || activeFilters.categorias.length === 0 || activeFilters.categorias.includes(p.category_slug);
        const matchMarca = groupToIgnore === 'marcas' || activeFilters.marcas.length === 0 || activeFilters.marcas.includes(p.filters?.brand);
        const matchSector = groupToIgnore === 'sectores' || activeFilters.sectores.length === 0 || activeFilters.sectores.includes(p.filters?.sector);
        const matchDisp = groupToIgnore === 'disponibilidad' || activeFilters.disponibilidad.length === 0 || activeFilters.disponibilidad.includes(p.filters?.availability);
        const matchEspec = groupToIgnore === 'especialidades' || activeFilters.especialidades.length === 0 || p.filters?.specialties.some(s => activeFilters.especialidades.includes(s));
        return matchCat && matchMarca && matchSector && matchDisp && matchEspec;
      });
    };

    // Categorias options
    const prodsForCat = getProductsExcludingGroup('categorias');
    const catOptions = categories.map(cat => ({
      id: cat.slug,
      name: cat.name,
      count: prodsForCat.filter(p => p.category_slug === cat.slug).length
    }));

    // Marcas options
    const prodsForMarca = getProductsExcludingGroup('marcas');
    const brandsMap = {};
    products.forEach(p => { if (p.filters?.brand) brandsMap[p.filters?.brand] = 0; });
    prodsForMarca.forEach(p => { if (p.filters?.brand && brandsMap[p.filters?.brand] !== undefined) brandsMap[p.filters?.brand]++; });

    // Sectores options
    const prodsForSector = getProductsExcludingGroup('sectores');
    const sectorsMap = {};
    products.forEach(p => { if (p.filters?.sector) sectorsMap[p.filters?.sector] = 0; });
    prodsForSector.forEach(p => { if (p.filters?.sector && sectorsMap[p.filters?.sector] !== undefined) sectorsMap[p.filters?.sector]++; });

    // Especialidades options
    const prodsForEspec = getProductsExcludingGroup('especialidades');
    const specialMap = {};
    products.forEach(p => { p.filters?.specialties?.forEach(sp => specialMap[sp] = 0); });
    prodsForEspec.forEach(p => { p.filters?.specialties?.forEach(sp => { if (specialMap[sp] !== undefined) specialMap[sp]++; }); });

    // Disponibilidad options
    const prodsForDisp = getProductsExcludingGroup('disponibilidad');
    const dispMap = {};
    products.forEach(p => { if (p.filters?.availability) dispMap[p.filters?.availability] = 0; });
    prodsForDisp.forEach(p => { if (p.filters?.availability && dispMap[p.filters?.availability] !== undefined) dispMap[p.filters?.availability]++; });

    const formatMap = (map) => Object.entries(map)
      .map(([name, count]) => ({ id: name, name, count }))
      .sort((a,b) => b.count - a.count);

    return {
      categorias: catOptions.filter(c => c.count > 0 || activeFilters.categorias.includes(c.id)),
      marcas: formatMap(brandsMap).filter(c => c.count > 0 || activeFilters.marcas.includes(c.id)),
      sectores: formatMap(sectorsMap).filter(c => c.count > 0 || activeFilters.sectores.includes(c.id)),
      especialidades: formatMap(specialMap).filter(c => c.count > 0 || activeFilters.especialidades.includes(c.id)),
      disponibilidad: formatMap(dispMap).filter(c => c.count > 0 || activeFilters.disponibilidad.includes(c.id))
    };

  }, [products, categories, activeFilters]);

  const totalActive = Object.values(activeFilters).reduce((acc, curr) => acc + curr.length, 0);

  return (
    <aside className={styles.sidebar}>
      <button
        className={styles.sidebarToggle}
        onClick={() => setMobileOpen(prev => !prev)}
        aria-expanded={mobileOpen}
      >
        <span className={styles.sidebarToggleLabel}>
          <FilterIcon />
          Filtros de búsqueda {totalActive > 0 && `(${totalActive})`}
        </span>
        <ChevronIcon open={mobileOpen} />
      </button>

      {mobileOpen && (
        <nav className={styles.filterNav}>
          
          {totalActive > 0 && (
            <button 
              className={styles.clearFiltersBtn}
              onClick={onClearFilters}
              title="Borrar todas las selecciones"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" strokeLinejoin="miter"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
              Limpiar Filtros
            </button>
          )}

          <FilterSection 
            title="Categoría de Producto" 
            items={filterOptions.categorias} 
            activeItems={activeFilters.categorias} 
            onToggle={(vals) => onFilterChange('categorias', vals)} 
          />

          <FilterSection 
            title="Sector de Aplicación" 
            items={filterOptions.sectores} 
            activeItems={activeFilters.sectores} 
            onToggle={(vals) => onFilterChange('sectores', vals)} 
          />

          <FilterSection 
            title="Marcas" 
            items={filterOptions.marcas} 
            activeItems={activeFilters.marcas} 
            onToggle={(vals) => onFilterChange('marcas', vals)} 
          />

          <FilterSection 
            title="Certificaciones / Especialidad" 
            items={filterOptions.especialidades} 
            activeItems={activeFilters.especialidades} 
            onToggle={(vals) => onFilterChange('especialidades', vals)} 
          />

          <FilterSection 
            title="Disponibilidad" 
            items={filterOptions.disponibilidad} 
            activeItems={activeFilters.disponibilidad} 
            onToggle={(vals) => onFilterChange('disponibilidad', vals)} 
          />

        </nav>
      )}
    </aside>
  );
};

export default PanelFiltros;
